import React from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  Tooltip,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowForwardIos as ArrowIcon,
  PhotoLibraryOutlined as ImagesIcon,
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/format';

// ตารางรายการรอตรวจสอบ OCR แยกตามวัน — แยกออกมาจาก UnverifiedExpenseDatesCard เป็น component
// ของตัวเอง ใช้สไตล์ UI ชุดเดียวกับ components/Table/ExpensesTableApplove.js (header แถบ
// bg-theme-600 + <h1 className="text-white">, ตาราง size="small", fallback เป็น Card บนมือถือ)
// คอลัมน์คนละชุดกับตารางเบิกจ่าย — ที่นี่มีแค่ วันที่ / จำนวนรายการ / ยอดรวม / สถานะ / ดูรูป
const formatDate = (value) => {
  const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('th-TH', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
};

function StatusChip({ verified }) {
  return (
    <Chip
      size="small"
      color={verified ? 'success' : 'warning'}
      label={verified ? 'ยืนยันแล้ว' : 'รอยืนยัน'}
      sx={{ height: 22 }}
    />
  );
}

export default function UnverifiedDatesTable({
  rows = [], onSelectDate, onViewImages, emptyLabel = 'No items',
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleViewImages = (event, date) => {
    event.stopPropagation();
    onViewImages(date);
  };

  if (isMobile) {
    return (
      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {rows.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <p>{emptyLabel}</p>
          </Box>
        ) : (
          rows.map((row, index) => (
            <Card
              key={row.date}
              onClick={() => onSelectDate(row.date)}
              sx={{ cursor: 'pointer' }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box>
                    <div className="text-xs text-gray-500">#{index + 1}</div>
                    <div className="font-semibold text-sm">{formatDate(row.date)}</div>
                  </Box>
                  <StatusChip verified={row.verified} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <Box>
                    <div className="text-xs text-gray-500">จำนวนรายการ</div>
                    <div className="text-sm">{Number(row.count || 0).toLocaleString('th-TH')} รายการ</div>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <div className="text-xs text-gray-500">ยอดรวม</div>
                    <div className="font-bold text-sm">{formatCurrency(row.totalAmount)} บาท</div>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Box>
                    <div className="text-xs text-gray-500">เงินเข้า (ค้าง)</div>
                    <div className="text-sm" style={{ color: '#15803d' }}>{formatCurrency(row.unverifiedIncomeAmount)} บาท</div>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <div className="text-xs text-gray-500">เงินออก (ค้าง)</div>
                    <div className="text-sm" style={{ color: '#dc2626' }}>{formatCurrency(row.unverifiedExpenseAmount)} บาท</div>
                  </Box>
                </Box>
                {row.verified && (
                  <Box sx={{ mt: 1, textAlign: 'right' }}>
                    <Tooltip title="ดูรูปที่ใช้ยืนยัน OCR">
                      <IconButton size="small" onClick={(event) => handleViewImages(event, row.date)} aria-label="ดูรูปที่ใช้ยืนยัน OCR">
                        <ImagesIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </Box>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow className="bg-theme-600">
            <TableCell align="center"><h1 className="text-white">No.</h1></TableCell>
            <TableCell align="center"><h1 className="text-white">วันที่</h1></TableCell>
            <TableCell align="center"><h1 className="text-white">จำนวนรายการ</h1></TableCell>
            <TableCell align="center"><h1 className="text-white">ยอดรวม (บาท)</h1></TableCell>
            <TableCell align="center"><h1 className="text-white">เงินเข้า (ค้าง)</h1></TableCell>
            <TableCell align="center"><h1 className="text-white">เงินออก (ค้าง)</h1></TableCell>
            <TableCell align="center"><h1 className="text-white">สถานะ</h1></TableCell>
            <TableCell align="center"><h1 className="text-white">ดูรูป</h1></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center">{emptyLabel}</TableCell>
            </TableRow>
          ) : (
            rows.map((row, index) => (
              <TableRow
                key={row.date}
                hover
                onClick={() => onSelectDate(row.date)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell align="center">{index + 1}</TableCell>
                <TableCell align="center"><h1 className="font-bold">{formatDate(row.date)}</h1></TableCell>
                <TableCell align="center">{Number(row.count || 0).toLocaleString('th-TH')}</TableCell>
                <TableCell align="center">{formatCurrency(row.totalAmount)}</TableCell>
                <TableCell align="center" sx={{ color: Number(row.unverifiedIncomeAmount) ? '#15803d' : '#cbd5e1' }}>
                  {Number(row.unverifiedIncomeAmount) ? formatCurrency(row.unverifiedIncomeAmount) : '-'}
                </TableCell>
                <TableCell align="center" sx={{ color: Number(row.unverifiedExpenseAmount) ? '#dc2626' : '#cbd5e1' }}>
                  {Number(row.unverifiedExpenseAmount) ? formatCurrency(row.unverifiedExpenseAmount) : '-'}
                </TableCell>
                <TableCell align="center"><StatusChip verified={row.verified} /></TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                    {row.verified ? (
                      <Tooltip title="ดูรูปที่ใช้ยืนยัน OCR">
                        <IconButton size="small" onClick={(event) => handleViewImages(event, row.date)} aria-label="ดูรูปที่ใช้ยืนยัน OCR">
                          <ImagesIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                    <ArrowIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
